import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useAuth } from '@/app/AuthContext.jsx';
import {
  acceptQuote,
  apiErrorName,
  assignCaseAssignee,
  calculatePricing,
  getActiveDiligencias,
  getActiveTask,
  getBankAccounts,
  getBillingProfiles,
  getCase,
  getCases,
  getCourts,
  getDashboardInicio,
  getLaneCounts,
  getNotificationActings,
  getOrgProfile,
  getPayments,
  getPricingTiers,
  getPricingUsage,
  getQuote,
  getQuotes,
  getTask,
  getTasks,
  getTribunals,
  listMembers,
  rejectQuote,
  submitFeedback,
  submitSupportRequest,
} from '@/lib/recepthor-api';
import { extractList, extractTotal } from '@/lib/apiList';
import { qk } from '@/lib/queryKeys';
import { isCausaSyncing } from '@/pages/causas/causaDisplay';
import { isDeadlineExpired } from '@/lib/dateUtils';
import { taskDiligenciaStage } from '@/lib/diligenciaStage';

// The pages' data layer: every page-level query/mutation lives here as a named
// hook, so the key (from qk) + fetcher + caching options are declared once and
// pages only decide WHEN to fetch (their `enabled` / paging state) and what UI
// to run on success/error. Keys stay in @/lib/queryKeys — hooks never inline
// key arrays.
//
// Every org-scoped wrapper call passes an explicit organizationId:
// hooks that receive it as an argument forward it; the rest read it from
// useAuth() (useLedgerQuery precedent). The orgContext singleton remains only
// as the transport-level fallback — see orgScope in lib/recepthor-api.js.

// Reads every page of a list, 100 rows each (the API's cap), until `total` is
// covered or an empty page arrives. A page that rejects propagates: a caller
// that counts, sums or indexes the whole set reading a partial one silently
// is worse than the query failing. MAX_PAGES is a safety stop for a `total`
// that keeps growing while rows arrive; hitting it throws instead of
// returning a list the caller would read as complete.
const PAGE_SIZE = 100;
const MAX_PAGES = 20;
export async function fetchAllPages(fetcher, params) {
  const results = [];
  let total = 0;
  for (let page = 1; page <= MAX_PAGES; page += 1) {
    const res = await fetcher({ ...params, page, pageSize: PAGE_SIZE });
    const list = extractList(res);
    results.push(...list);
    total = extractTotal(res, list);
    if (!list.length || results.length >= total) break;
    if (page === MAX_PAGES) {
      throw new Error(
        `fetchAllPages: ${total} rows declared, ${results.length} read after ${MAX_PAGES} pages.`,
      );
    }
  }
  // Same `{ pagination, results }` shape a single page has.
  return { pagination: { totalRecords: total }, results };
}

// ── Causas ──────────────────────────────────────────────────────────────────

// A causa arrives "Sincronizando" and the scraper settles it, so the list shows a
// value no user action changes. It re-asks while any row is still moving and stops
// on its own once none is. refetchInterval pauses while the tab is hidden
// (refetchIntervalInBackground defaults to false), so a forgotten tab costs
// nothing.
const CAUSA_SYNC_POLL_MS = 15_000;

function causasStillSyncing(payload) {
  return extractList(payload).some((c) => isCausaSyncing(c.verificationStatus));
}

// A single causa by id. Used by the causa detail view and the diligencia view.
// The rejection propagates. Swallowed, a failed read answered null, and the
// detail renders "Causa no encontrada" for null: a statement that the rol
// belongs to no subscribed causa, made about a read that never arrived. During
// a sync that is the likeliest moment for it.
//
// It polls on the same interval as useCasesList while the case is still
// syncing, and stops at a terminal status: PJUD settles the verification
// status, and no user action signals when it does.
export function useCaseDetail(caseId, { enabled = Boolean(caseId) } = {}) {
  const organizationId = useAuth()?.organizationId ?? null;
  return useQuery({
    queryKey: qk.caseDetail(caseId),
    queryFn: () => getCase(caseId, { organizationId }),
    enabled,
    refetchInterval: (query) =>
      isCausaSyncing(query.state.data?.verificationStatus)
        ? CAUSA_SYNC_POLL_MS
        : false,
  });
}

// Server-paginated /cases list (the Causas table). `tab` maps to the server
// params legalStatus/assignee and is folded into the key so each tab caches its
// own pages. placeholderData keeps the previous page's rows on screen while the
// next page of the same tab loads, so paging doesn't flash skeletons. A tab
// change shows the loading state instead: the server filters by tab, so the
// previous tab's rows would render as members of the new one.
//
// Errors propagate (no catch) so the page renders its error state instead of the
// ordinary empty table. Swallowing them made a rejected request indistinguishable
// from an org with no causas: the list vanished and nothing said why, which is how
// an invalid sort name went three weeks undiagnosed. Same reasoning as
// usePagosCola.
export function useCasesList({ organizationId, tab, ...params }) {
  return useQuery({
    queryKey: qk.cases.list({ organizationId, tab, ...params }),
    queryFn: () =>
      getCases({
        ...params,
        organizationId,
        // active=true applies the server's active-case criterion, the same one
        // the Inicio tile counts, so the tab and the tile show the same set.
        active: tab === 'activas' ? 'true' : undefined,
        legalStatus: tab === 'terminadas' ? 'Terminada' : undefined,
        assignee: tab === 'asignadas' ? 'me' : undefined,
      }),
    enabled: organizationId != null,
    placeholderData: (previousData, previousQuery) =>
      previousQuery?.queryKey[1]?.tab === tab ? previousData : undefined,
    refetchInterval: (query) =>
      causasStillSyncing(query.state.data) ? CAUSA_SYNC_POLL_MS : false,
  });
}

// Causa picker (Nueva diligencia): search when the term is 2+ chars, otherwise
// the most recent causas. The '__all__' sentinel keys the unsearched list.
export function useCasesPick(term, { enabled = true } = {}) {
  const organizationId = useAuth()?.organizationId ?? null;
  const hasQuery = term.length >= 2;
  return useQuery({
    queryKey: qk.cases.pick(hasQuery ? term : '__all__'),
    queryFn: () =>
      getCases(
        hasQuery
          ? { page: 1, pageSize: 20, search: term, organizationId }
          : {
              page: 1,
              pageSize: 50,
              sortBy: 'desc',
              sortKey: 'createdAt',
              organizationId,
            },
      ),
    enabled,
  });
}

// PJUD actings on one case (the "Actuaciones" tab / feed), newest 50.
//
// The rejection propagates, for the same reason as the causa detail: swallowed,
// a failed read renders "Sin actuaciones registradas", which is what a causa the
// scraper has not filled yet also renders. Those are different facts and the
// second is the one the sync is waiting to change.
export function useNotificationActings(
  caseId,
  { enabled = Boolean(caseId) } = {},
) {
  const organizationId = useAuth()?.organizationId ?? null;
  return useQuery({
    queryKey: qk.notificationActings.list({ caseId, page: 1, pageSize: 50 }),
    queryFn: () =>
      getNotificationActings({
        caseId,
        page: 1,
        pageSize: 50,
        organizationId,
      }),
    enabled,
  });
}

// Assign / change the causa's encargado → PATCH /cases/:caseId/assignee.
// Snapshots the cases list for rollback, refreshes the list + the detail (the
// meta band shows the new encargado). UI feedback stays with the caller.
export function useAssignCaseAssigneeMutation(
  caseId,
  { onSuccess, onError } = {},
) {
  const organizationId = useAuth()?.organizationId ?? null;
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ assigneeId }) =>
      assignCaseAssignee(caseId, assigneeId, { organizationId }),
    onMutate: async () => {
      const prev = queryClient.getQueryData(qk.cases.all());
      return { prev };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.cases.all() });
      // Refresh the detail so the meta band shows the new encargado without a
      // full reload (the name is derived from GET /cases/:id → members).
      queryClient.invalidateQueries({ queryKey: qk.caseDetail(caseId) });
      onSuccess?.();
    },
    onError: (err, _vars, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(qk.cases.all(), ctx.prev);
      onError?.(err);
    },
  });
}

// ── Diligencias / Tasks ─────────────────────────────────────────────────────

// One task by id. GET /tasks/:id is org-scoped: a foreign / missing task 404s
// (no retry, no swallow) so the page can render a clean "no encontrada".
// An offer or a payment settling never refreshed /diligencia on its own.
// Polls on the same interval as useCaseDetail while the task is active, and
// stops once taskDiligenciaStage reads it as 'cerrada'. A first load that
// failed stops it too: a 404 or a 403 does not heal by repeating it, and
// retry: false does not stop the interval.
export function useTaskDetail(taskId, { enabled = Boolean(taskId) } = {}) {
  const organizationId = useAuth()?.organizationId ?? null;
  return useQuery({
    queryKey: qk.taskDetail(taskId),
    queryFn: () => getTask(taskId, { organizationId }),
    enabled,
    retry: false,
    refetchInterval: (query) => {
      if (query.state.status === 'error' && !query.state.data) return false;
      return taskDiligenciaStage(query.state.data).stage !== 'cerrada'
        ? CAUSA_SYNC_POLL_MS
        : false;
    },
  });
}

// Raw /tasks list with the caller's params verbatim (key = params).
export function useTasksList(params, { enabled = true } = {}) {
  const organizationId = useAuth()?.organizationId ?? null;
  return useQuery({
    queryKey: qk.tasks.list(params),
    queryFn: () => getTasks({ ...params, organizationId }),
    enabled,
  });
}

// Every task matching params, across pages, for a caller that indexes tasks
// by id. With one page, a task past the first 100 would never get its columns.
export function useAllTasksList(params, { enabled = true } = {}) {
  const organizationId = useAuth()?.organizationId ?? null;
  return useQuery({
    queryKey: qk.tasks.list({ ...params, organizationId, all: true }),
    queryFn: () => fetchAllPages(getTasks, { ...params, organizationId }),
    enabled,
  });
}

// Active-lane tasks (board + table + dashboard) — unfiltered, deadline-first;
// grouping by stage happens client-side. Extra RQ options (e.g.
// placeholderData) pass through.
//
// A rejection propagates rather than reading as a quiet "no tasks": the
// query's own `isError` carries the failure, and neither consumer treats a
// render-time throw as a risk (the app's QueryClient sets no
// `throwOnError`), so there is no boundary to protect by swallowing it.
export function useActiveLaneTasks(organizationId, options = {}) {
  return useQuery({
    queryKey: qk.tasks.activeLane({ organizationId }),
    queryFn: () =>
      fetchAllPages(getTasks, { sort: 'deadline:asc', organizationId }),
    enabled: organizationId != null,
    ...options,
  });
}

// Historial — the server lane, paged + searched. placeholderData keeps the
// previous page's rows while the next one loads.
export function useTasksHistorial(
  { organizationId, page, pageSize, q },
  { enabled = organizationId != null } = {},
) {
  return useQuery({
    queryKey: qk.tasks.historial({ organizationId, page, pageSize, q }),
    queryFn: () =>
      getTasks({
        lane: 'historial',
        page,
        pageSize,
        search: q || undefined,
        sort: 'updatedAt:desc',
        organizationId,
      }),
    enabled,
    placeholderData: keepPreviousData,
  });
}

// Recent tasks (newest 50) — feeds the comuna·región "recents" suggestions.
export function useRecentTasks() {
  const organizationId = useAuth()?.organizationId ?? null;
  return useQuery({
    queryKey: qk.tasks.recentComunas(),
    queryFn: () => getTasks({ page: 1, pageSize: 50, organizationId }),
  });
}

// Board lane tallies (Activas / Historial counts).
export function useLaneCounts(organizationId, { enabled } = {}) {
  return useQuery({
    queryKey: qk.laneCounts({ organizationId }),
    queryFn: () => getLaneCounts({ organizationId }),
    enabled: enabled ?? organizationId != null,
  });
}

// Active diligencias across the org (one slim query; no server params). The
// caller's params object only scopes the cache key (e.g. { organizationId });
// the request itself is pinned to the session's active org.
export function useActiveDiligencias(params, { enabled = true } = {}) {
  const organizationId = useAuth()?.organizationId ?? null;
  return useQuery({
    queryKey: qk.activeDiligencias(params),
    queryFn: () => getActiveDiligencias({ organizationId }),
    enabled,
  });
}

// The one active task on a causa (aside card). One active diligencia per causa.
export function useActiveTaskByCase(
  caseId,
  { enabled = Boolean(caseId) } = {},
) {
  const organizationId = useAuth()?.organizationId ?? null;
  return useQuery({
    queryKey: qk.activeTask.byCase(caseId),
    queryFn: () =>
      getActiveTask({ caseId, page: 1, pageSize: 1, organizationId }).catch(
        () => null,
      ),
    enabled,
  });
}

// ── Quotes / Payments ───────────────────────────────────────────────────────

export function useQuotesByTask(taskId, { enabled = Boolean(taskId) } = {}) {
  const organizationId = useAuth()?.organizationId ?? null;
  return useQuery({
    queryKey: qk.quotes.byTask(taskId),
    // A mass send to every receptor of a court outgrows one page, and an
    // accepted quote past it would vanish from the diligence view.
    queryFn: () => fetchAllPages(getQuotes, { taskId, organizationId }),
    enabled,
  });
}

// Pendientes read nearest deadline first; GET /quotes cannot sort by it, so
// the page is re-sorted here. Quotes whose deadline already passed go after
// the ones still open, and a missing or unreadable deadline goes last.
function byNearestDeadline(payload, now = Date.now()) {
  const deadline = (quote) => {
    const at = quote.taskDeadline ? Date.parse(quote.taskDeadline) : NaN;
    return Number.isNaN(at) ? Infinity : at;
  };
  // Expired the same way the card badge reads it: taskDeadlineExpiresAt
  // (midnight in Chile) when the API sends it, else the deadline instant.
  const rank = (quote) => {
    const at = deadline(quote);
    if (at === Infinity) return 2;
    const expired =
      isDeadlineExpired(quote.taskDeadline, quote.taskDeadlineExpiresAt, now) ??
      at < now;
    return expired ? 1 : 0;
  };
  const results = [...extractList(payload)].sort(
    (a, b) => rank(a) - rank(b) || deadline(a) - deadline(b) || 0,
  );
  return { ...payload, results };
}

export function useReceptorInbox(organizationId, { enabled = true } = {}) {
  return useQuery({
    queryKey: qk.quotes.inbox({ organizationId }),
    queryFn: () =>
      fetchAllPages(getQuotes, {
        organizationId,
        sortBy: 'desc',
        sortKey: 'createdAt',
        status: 'INVITED',
        // Cancelled tasks leave quotes INVITED; exclude them so pendientes
        // do not keep Cotizar/Rechazar for abandoned diligencias.
        scope: 'notAbandoned',
      }).then(byNearestDeadline),
    enabled: enabled && organizationId != null,
  });
}

// Respondidas: what the receptor already answered. GET /quotes filters one
// status at a time, so the statuses are read separately and merged, newest
// change first. New paid offers stay OFFERED with paidAt, but older rows were
// backfilled as PAID and are still stored, so PAID is read too. The API
// rewrite retires that status: once the API refuses it as invalid, the PAID
// read counts as empty instead of failing the whole view. Any other failure,
// on PAID or the rest, still fails it.
const ANSWERED_STATUSES = ['OFFERED', 'DECLINED'];
const LEGACY_ANSWERED_STATUSES = ['PAID'];

// How the API refuses a status it no longer knows: the query fails its
// schema with a 400 ValidationError. Any other failure is real and surfaces.
function isRetiredStatusError(err) {
  return err?.status === 400 && apiErrorName(err.body) === 'ValidationError';
}

export function useReceptorAnswered(organizationId, { enabled = true } = {}) {
  return useQuery({
    queryKey: qk.quotes.answered({ organizationId }),
    queryFn: async () => {
      const read = (status) =>
        fetchAllPages(getQuotes, {
          organizationId,
          sortBy: 'desc',
          sortKey: 'updatedAt',
          status,
          scope: 'notAbandoned',
        });
      const lists = await Promise.all([
        ...ANSWERED_STATUSES.map(read),
        ...LEGACY_ANSWERED_STATUSES.map((status) =>
          read(status).catch((err) => {
            if (isRetiredStatusError(err)) return { results: [] };
            throw err;
          }),
        ),
      ]);
      // A quote that changes status between the reads could come twice.
      const byId = new Map(
        lists.flatMap((list) => extractList(list)).map((q) => [q.id, q]),
      );
      const results = [...byId.values()].sort((a, b) =>
        String(b.updatedAt).localeCompare(String(a.updatedAt)),
      );
      // Same `{ pagination, results }` shape the other inbox reads.
      return { pagination: { totalRecords: results.length }, results };
    },
    enabled: enabled && organizationId != null,
  });
}

export function useQuoteDetail(quoteId, organizationId) {
  return useQuery({
    queryKey: qk.quotes.detail({ organizationId, quoteId }),
    queryFn: () => getQuote(quoteId, { organizationId }),
    enabled: Boolean(quoteId) && organizationId != null,
  });
}

export function useAcceptQuote(organizationId) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => acceptQuote(payload, { organizationId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.quotes.all() });
    },
  });
}

export function useRejectQuote(organizationId) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => rejectQuote(payload, { organizationId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.quotes.all() });
    },
  });
}

// The API archives its own comprobante a few seconds after the charge settles,
// best-effort, so a payer who comes back from the provider can read the PAYIN
// before the PDF exists. With awaitReceipt the read repeats every
// RECEIPT_POLL_MS while a recently settled PAYIN has no comprobante, and gives
// up after RECEIPT_POLL_MAX_FETCHES reads (about two minutes). The cap counts
// reads rather than elapsed time: refetchInterval pauses while the tab is
// hidden, so a hidden tab does not burn it.
//
// A live PAYIN (not FAILED) with no comprobante is pending when its status is
// not SUCCESS yet (a row read before the settlement: the caller says the
// charge is paid, so it has to be read again) or when it is SUCCESS and
// settledAt is within RECEIPT_SETTLED_WINDOW_MS. Generation is best-effort and
// is not retried, so a SUCCESS settled long ago (or with no settledAt) will not
// get a comprobante by waiting: nothing is being generated there, and claiming
// so would be false. A row that never reaches SUCCESS ends at the read cap.
const RECEIPT_POLL_MS = 5_000;
const RECEIPT_POLL_MAX_FETCHES = 24;
const RECEIPT_SETTLED_WINDOW_MS = 10 * 60_000;

function receiptPending(payload) {
  const payin = extractList(payload).find(
    (p) => p.type === 'PAYIN' && p.status !== 'FAILED',
  );
  if (payin == null || payin.hasReceipt === true) return false;
  if (payin.status !== 'SUCCESS') return true;
  const settledAt = payin.settledAt ? Date.parse(payin.settledAt) : NaN;
  return (
    Number.isFinite(settledAt) &&
    Date.now() - settledAt < RECEIPT_SETTLED_WINDOW_MS
  );
}

// Movements for one task — the PAYIN row anchors the paid phases. Newest
// first so a retried payment leads the list. Only PAYIN is read here, and the
// server does the filtering: a task also accumulates payout and refund rows,
// and one page of every type can push the charge out of the result. Gated on
// the resolved org: a fresh boot renders before useAuth settles, and firing
// then sends no X-Organization-Id, which the API refuses.
//
// `receiptPolling` is true while the re-read for the comprobante is running:
// the page shows "Generando comprobante" for as long as it holds.
export function usePaymentsByTask(
  taskId,
  { enabled = Boolean(taskId), awaitReceipt = false } = {},
) {
  const organizationId = useAuth()?.organizationId ?? null;
  const queryClient = useQueryClient();
  const queryKey = qk.payments.byTask(taskId);
  const query = useQuery({
    queryKey,
    queryFn: () =>
      getPayments({
        taskId,
        type: 'PAYIN',
        page: 1,
        pageSize: 20,
        sortKey: 'createdAt',
        sortBy: 'desc',
        organizationId,
      }),
    // The rejection propagates. Swallowing it made a failed read look like
    // "this diligencia has no payment", which on a paid phase erases the
    // receptor, the total and the comprobante with nothing saying why; it also
    // cancelled the query's own retries. Same reasoning as useCasesList.
    enabled: enabled && organizationId != null,
    // Stops on its own at the first of: the comprobante arrived, a read failed
    // (a 403 or a 404 does not heal by repeating it), or the cap.
    refetchInterval: (q) =>
      awaitReceipt &&
      q.state.status !== 'error' &&
      receiptPending(q.state.data) &&
      q.state.dataUpdateCount < RECEIPT_POLL_MAX_FETCHES
        ? RECEIPT_POLL_MS
        : false,
  });
  const receiptPolling =
    awaitReceipt &&
    !query.isError &&
    query.fetchStatus !== 'paused' &&
    receiptPending(query.data) &&
    (queryClient.getQueryState(queryKey)?.dataUpdateCount ?? 0) <
      RECEIPT_POLL_MAX_FETCHES;
  return { ...query, receiptPolling };
}

// Fee breakdown (GET /pricing/calculate) — the ONLY consumer of qk.pricing:
// CheckoutModal and the pay panels all go through this hook, so their numbers
// match and the cache entry is pinned to the org that fetched it.
export function usePricing({ enabled = true } = {}) {
  const organizationId = useAuth()?.organizationId ?? null;
  return useQuery({
    queryKey: qk.pricing(organizationId),
    queryFn: () => calculatePricing({ organizationId }),
    enabled,
  });
}

// Canonical fee-tier table (GET /pricing/tiers, public) and the org's
// charge-side usage (GET /pricing/usage): the Tarifa tab reads both, so the
// tramo it shows is the one the next notification pays. Errors propagate on
// both (no catch) so the tab renders an honest placeholder instead of a
// falsely highlighted tramo.
export function usePricingTiers({ enabled = true } = {}) {
  return useQuery({
    queryKey: qk.pricingTiers(),
    queryFn: getPricingTiers,
    enabled,
  });
}

export function usePricingUsage({ enabled = true } = {}) {
  const organizationId = useAuth()?.organizationId ?? null;
  return useQuery({
    queryKey: qk.pricingUsage({ organizationId }),
    queryFn: () => getPricingUsage({ organizationId }),
    enabled: enabled && organizationId != null,
  });
}

// ── PJUD directory ──────────────────────────────────────────────────────────

// Cortes de Apelaciones directory. One page covers the 17 courts, within the
// API's cap of 100. The Causas and Tribunales filters and the Receptores
// explorer share this fetch and its cache.
export function useCourts({ enabled = true } = {}) {
  const organizationId = useAuth()?.organizationId ?? null;
  return useQuery({
    queryKey: qk.courts(),
    queryFn: () => getCourts({ page: 1, pageSize: 100, organizationId }),
    enabled,
  });
}

// Tribunal directory (first 100, name-sorted) — the Causas tribunal filter.
export function useTribunals({ enabled = true } = {}) {
  const organizationId = useAuth()?.organizationId ?? null;
  return useQuery({
    queryKey: qk.tribunals({ sortKey: 'tribunalName' }),
    // Hundreds of tribunals outgrow one page, and the /causas filter needs all
    // of them.
    queryFn: () =>
      fetchAllPages(getTribunals, {
        sortKey: 'tribunalName',
        sortBy: 'asc',
        organizationId,
      }),
    enabled,
  });
}

// ── Dashboard ───────────────────────────────────────────────────────────────

// Inicio aggregate: PJUD actuaciones feed + active-case count, windowed by
// feedRange ({ startDate }). Errors surface (no swallow) so the feed can show
// an error state.
// `enabled` lets the caller skip the request altogether (the endpoint needs
// org:read-dashboard, which not every role has).
export function useDashboardInicio(
  organizationId,
  feedRange,
  { enabled = true } = {},
) {
  return useQuery({
    queryKey: qk.dashboardInicio({ organizationId, ...feedRange }),
    queryFn: () => getDashboardInicio({ ...feedRange, organizationId }),
    enabled: organizationId != null && enabled,
  });
}

// ── Pagos (lawyer) ──────────────────────────────────────────────────────────

// Cola de pago — the offers receptores actually made, org-wide (grouping by
// diligencia is client-side; payment is exclusive per diligencia).
// Errors propagate (no catch) so the page renders its error state instead of a
// silent empty table — the same failure mode a swallowed 4xx used to hide.
export function usePagosCola(organizationId, { enabled } = {}) {
  return useQuery({
    queryKey: qk.pagos.cola({ organizationId }),
    queryFn: () =>
      fetchAllPages(getQuotes, {
        // Explicit org: don't trust the orgContext side-channel alone
        // (OrganizationHeaderMissing regression — see useComprobantesPago).
        organizationId,
        status: 'OFFERED',
        // Payable stages only: a cancelled task leaves its quotes OFFERED.
        scope: 'payable',
        sortKey: 'createdAt',
        sortBy: 'desc',
      }),
    enabled: enabled ?? organizationId != null,
  });
}

// En proceso — the org's PAYIN movements, all statuses; the page keeps the
// unconfirmed ones client-side.
// Errors propagate (no catch) so the page renders its error state instead of
// a silent empty table that reads like an empty org.
export function usePagosProceso(organizationId, { enabled } = {}) {
  return useQuery({
    queryKey: qk.pagos.proceso({ organizationId }),
    queryFn: () =>
      fetchAllPages(getPayments, {
        organizationId,
        type: 'PAYIN',
        sortKey: 'createdAt',
        sortBy: 'desc',
      }),
    enabled: enabled ?? organizationId != null,
  });
}

// Pagadas — provider-confirmed payins (PAYIN + SUCCESS), newest first. The tab
// turns on the cobro, not on the payout: a paid diligencia reads as "Pagada"
// while the manual payout to the receptor is still pending (same criterion as
// Comprobantes de pago).
// Errors propagate (no catch) so the page renders its error state instead of
// a silent empty table that reads like an empty org.
export function usePagosHistorial(organizationId, { enabled } = {}) {
  return useQuery({
    queryKey: qk.pagos.historial({ organizationId }),
    queryFn: () =>
      fetchAllPages(getPayments, {
        organizationId,
        type: 'PAYIN',
        status: 'SUCCESS',
        sortKey: 'updatedAt',
        sortBy: 'desc',
      }),
    enabled: enabled ?? organizationId != null,
  });
}

// Comprobantes de pago — the payins the lawyer made, newest first, filtered
// server-side with type=PAYIN&status=SUCCESS: SUCCESS on the movement
// expresses "provider-confirmed" directly, independent of the payout's
// progress. Errors propagate (no catch) so the page renders its error state.
export function useComprobantesPago(organizationId, { enabled } = {}) {
  return useQuery({
    queryKey: qk.pagos.comprobantes({ organizationId }),
    queryFn: () =>
      fetchAllPages(getPayments, {
        // Explicit org: the query is gated on this value, so the request must
        // carry it too — not the orgContext side-channel, which can be unset
        // when this fires (OrganizationHeaderMissing regression).
        organizationId,
        type: 'PAYIN',
        status: 'SUCCESS',
        sortKey: 'updatedAt',
        sortBy: 'desc',
      }),
    enabled: enabled ?? organizationId != null,
  });
}

// ── Org settings / billing ──────────────────────────────────────────────────

// Feedback / support message (POST /feedback). Caches nothing; toasts and
// per-call onSuccess/onError stay with the caller (they differ per form).
export function useSubmitFeedbackMutation() {
  const organizationId = useAuth()?.organizationId ?? null;
  return useMutation({
    mutationFn: (payload) => submitFeedback(payload, { organizationId }),
  });
}

// Support message (POST /support-requests). Reaches the support mailbox as an
// email with the sender as reply-to, which is what a question needs. Distinct
// from useSubmitFeedbackMutation, whose endpoint opens a tracker card instead.
export function useSubmitSupportRequestMutation() {
  const organizationId = useAuth()?.organizationId ?? null;
  return useMutation({
    mutationFn: (message) => submitSupportRequest(message, { organizationId }),
  });
}

// Org members (admin-gated endpoint) — encargado selects + the members table.
export function useMembers(
  organizationId,
  { enabled = organizationId != null } = {},
) {
  return useQuery({
    queryKey: qk.members(organizationId),
    queryFn: () => listMembers({ organizationId }),
    enabled,
  });
}

// Billing profiles (datos tributarios) of the active org.
export function useBillingProfiles(
  organizationId,
  { enabled = organizationId != null } = {},
) {
  return useQuery({
    queryKey: qk.billingProfiles.list({ organizationId }),
    queryFn: () => getBillingProfiles({ organizationId }),
    enabled,
  });
}

// Org DisplayProfile (Configuración → Perfil).
export function useOrgProfile(
  organizationId,
  { enabled = organizationId != null } = {},
) {
  return useQuery({
    queryKey: qk.orgProfile.detail({ organizationId }),
    queryFn: () => getOrgProfile({ organizationId }),
    enabled,
  });
}

// Bank accounts of the active org.
export function useBankAccounts(
  organizationId,
  { enabled = organizationId != null } = {},
) {
  return useQuery({
    queryKey: qk.bankAccounts.list({ organizationId }),
    queryFn: () => getBankAccounts({ organizationId }),
    enabled,
  });
}
